import { useState, useRef, useEffect } from "react";
import { IconButton, Box, Typography, Chip, Paper, Grid, Divider } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import NotificationsIcon from "@mui/icons-material/Notifications";
import webinar from "../../assets/common/notifications/webinar.svg";
import lateSAT from "../../assets/common/notifications/lateSAT.svg";
import internalFail from "../../assets/common/notifications/internalFail.svg";
import launch from "../../assets/common/notifications/launch.svg";
import maintence from "../../assets/common/notifications/maintence.svg";
import lateCommerce from "../../assets/common/notifications/lateCommerce.svg";
import noResults from "../../assets/common/notifications/noResults.svg";
import ModalInfo from "../common/Modals/modalInfo";
import { useTranslation } from "react-i18next";
import {
  GetClientNotifications,
  ReadClientNotifications,
  GetClientAlarms,
  ReadClientAlarms,
} from "../../services/user/notifications";
import { CODES } from "../../consts/codes";
import { getUser } from "../../pages/userSlice";
import { useSelector } from "react-redux";
import { openQlikSense } from "../../helpers/helpers";
import { getAlarmTypeImage } from "../../utils/alarmTypeImage";

// Mini componente para mostrar una notificación genérica
const NotificationCard = ({ notification, fetchData }) => {
  const [hover, setHover] = useState(false);
  const userInfo = useSelector(getUser);

  const handleMouseEnter = () => setHover(true);
  const handleMouseLeave = () => setHover(false);
  const { t } = useTranslation();

  /**
   * Función que devuelve la imagen correspondiente a la notificación según su tipo.
   * @return {string} URL de la imagen de la notificación
   */
  const handleSetImage = () => {
    switch (notification.notificationType) {
      case "Demora SAT":
        return lateSAT;
      case "Falla interna":
        return internalFail;
      case "Lanzamientos":
        return launch;
      case "Webinar":
        return webinar;
      case "Demora Comercios":
        return lateCommerce;
      case "Mantenimiento":
        return maintence;
      default:
        return webinar;
    }
  };

  /** Traduce el tipo de notificación al idioma configurado. */
  const translateNotificationType = (type) => {
    const value = type.toUpperCase();
    switch (value) {
      case "DEMORA COMERCIOS":
        return t("App.notifications.filter.typesNotifications.delay");
      case "DEMORA SAT":
        return t("App.notifications.filter.typesNotifications.sat");
      case "FALLA INTERNA":
        return t("App.notifications.filter.typesNotifications.internalFail");
      case "MANTENIMIENTO":
        return t("App.notifications.filter.typesNotifications.maintenance");
      case "WEBINAR":
        return t("App.notifications.filter.typesNotifications.webinar");
      case "LANZAMIENTOS":
        return t("App.notifications.filter.typesNotifications.update");
      default:
        return type;
    }
  };

  /**
   * Al hacer click en la notificación, se marca como leída y se abre el enlace si existe.
   */
  const handleNotificationClick = () => {
    onRead();
    if (notification.link) window.open("https://" + notification.link, "_blank");
  };

  /**
   * Función para marcar una notificación como leída tras hacer clic en ella
   * y refrescar la lista de notificaciones.
   */
  const onRead = async () => {
    if (notification.read === "NO" || notification.read === null) {
      const payload = {
        country: userInfo.company.country,
        pk: notification.pk,
        sk: notification.sk,
      };

      try {
        await ReadClientNotifications(payload);
        fetchData();
      } catch (error) {
        //TODO: Falta implementar inserción de log en dynamodb
      }
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        bgcolor:
          notification.read === "SI"
            ? hover
              ? "#F4F6FA" // Fondo un poco más claro al hacer hover si está leída
              : "#F8F8F8" // Fondo gris claro para notificaciones leídas
            : "#F1EDFE", // Fondo morado para no leídas
        borderRadius: "12px",
        display: "flex",
        alignItems: { xs: "center", sm: "flex-start" }, // Centrar contenido en móviles
        flexDirection: { xs: "column", sm: "row" }, // Cambia a columna en móviles
        justifyContent: { xs: "center", sm: "flex-start" }, // Justifica al centro en móviles
        width: "100%",
      }}
      onMouseEnter={notification.read === "SI" ? handleMouseEnter : null}
      onMouseLeave={notification.read === "SI" ? handleMouseLeave : null}
      onClick={handleNotificationClick}
    >
      {/* Imagen de la notificación */}
      <Box
        component="img"
        src={handleSetImage()}
        alt="Notification"
        sx={{
          width: 90, // Imagen más pequeña en móviles
          height: { xs: "auto", sm: 100 }, // Mantiene proporciones en móviles
          borderRadius: "8px",
          mb: { xs: 2, sm: 0 }, // Margen abajo solo en móviles
          mr: { sm: 2 }, // Margen derecho solo en pantallas más grandes
        }}
      />
      {/* Contenido de la notificación */}
      <Box
        sx={{
          textAlign: { xs: "center", sm: "left" }, // Texto centrado en móviles
          width: { xs: 300, sm: 210 },
        }}
      >
        <Typography
          variant="subtitle1"
          className="display-notification__primary-one"
          sx={{ mb: { xs: 1, sm: 0 } }} // Margen abajo solo en móviles
        >
          {translateNotificationType(notification.notificationType)}
        </Typography>
        <Typography
          variant="body2"
          className="subtitle-notification__primary-one"
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            textAlign: { xs: "center", sm: "left" }, // Centrado solo en móviles
            paddingLeft: "0px",
          }}
        >
          {notification.description}
        </Typography>
      </Box>
    </Paper>
  );
};

// Mini componente para mostrar una notificación de alerta específica
const AlertNotificationCard = ({ alarm, fetchData }) => {
  const userInfo = useSelector(getUser);
  const [hover, setHover] = useState(false);
  const { i18n } = useTranslation();
  const isSpanish = (i18n.language || "").toLowerCase().startsWith("es");

  // Título a mostrar
  const alarmTitle = isSpanish ? alarm.name : alarm.nameEnglish || alarm.name;

  const handleMouseEnter = () => setHover(true);
  const handleMouseLeave = () => setHover(false);

  const fullNameUser = `${userInfo.firstName} ${userInfo.lastName}`;
  const emailUser = userInfo.email;

  /** Devuelve la imagen de la alarma según su tipo. */
  const handleSetAlarmImage = () => getAlarmTypeImage(alarm.idTypeAlarm);

  /** Marca la alarma como leída. */
  const onReadAlarm = async () => {
    if (alarm.alarmRead === "NO" || alarm.alarmRead === null) {
      const payload = {
        eanCompany: userInfo.company.eanCompany,
        user: userInfo.email,
        idTypeAlarm: alarm.idTypeAlarm,
      };

      try {
        await ReadClientAlarms(payload);
        fetchData();
      } catch (error) {
        //TODO: Falta implementar inserción de log en dynamodb
      }
    }
  };

  /** Maneja el clic en una alarma para marcarla como leída y abrir su enlace. */
  const handleAlarmClick = () => {
    onReadAlarm();
    if (alarm.url) openQlikSense(fullNameUser, emailUser, alarm.url);
  };

  const isRead = alarm.alarmRead === "SI";
  const bgColor = isRead ? (hover ? "#F4F6FA" : "#F8F8F8") : "#F1EDFE";

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        bgcolor: bgColor,
        borderRadius: "12px",
        display: "flex",
        gap: 2,
        alignItems: { xs: "center", sm: "flex-start" },
        flexDirection: { xs: "column", sm: "row" },
        cursor: "pointer",
        width: "100%",
      }}
      onMouseEnter={isRead ? handleMouseEnter : undefined}
      onMouseLeave={isRead ? handleMouseLeave : undefined}
      onClick={handleAlarmClick}
    >
      {/* Imagen izquierda */}
      <Box
        component="img"
        src={handleSetAlarmImage()}
        alt="Alarm type"
        sx={{
          width: 90,
          height: 100,
          borderRadius: "8px",
          objectFit: "contain",
          mb: { xs: 1, sm: 0 },
        }}
      />

      {/* Contenido (título + bullets) */}
      <Box sx={{ width: "100%" }}>
        <Typography
          className="display-notification__primary-one"
          sx={{
            color: "#553ab6",
            textAlign: { xs: "center", sm: "left" },
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          {alarmTitle}
        </Typography>

        {/* Bullets como en la imagen */}
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: { xs: 2, sm: 2.5 },
            textAlign: { xs: "left", sm: "left" },
          }}
        >
          {(alarm.lstAlarms || []).map((item, idx) => (
            <Box component="li" key={`${alarm.idTypeAlarm}-${idx}`} sx={{ color: "#553ab6" }}>
              <Typography
                variant="body2"
                className="subtitle-notification__primary-one"
                sx={{ color: "#553ab6", display: "inline" }}
              >
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

const NotificationIcon = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const [notificationList, setNotificationList] = useState([]);
  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [titleModalInfo, setTitleModalInfo] = useState("");
  const [contentModalInfo, setContentModalInfo] = useState({});
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const iconRef = useRef(null);
  const userInfo = useSelector(getUser);

  /**
   * Función que abre la ventana de listado de notificaiones
   */
  const handleToggleNotifications = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Función que cierra la ventana de listado de notificaiones
   */
  const handleCloseNotifications = () => {
    setIsOpen(false);
  };

  /**
   * UseEffect que permite ubicar el dropdown de notificaciones justo debajo del icono
   */
  useEffect(() => {
    if (iconRef.current) {
      const iconRect = iconRef.current.getBoundingClientRect();
      setDropdownTop(iconRect.bottom);
    }
  }, [isOpen]);

  /**
   * UseEffect que permite obtener las notificaciones y alarmas al iniciar el componente
   */
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  /**
   * Función que determina el tipo de mensaje a mostrar en el modal de información según las notificaciones no leídas.
   */
  const handleMessageType = (unreadNotifications) => {
    const hasDevolution = unreadNotifications.some((notification) =>
      notification.idTypeAlarm.toLowerCase().includes("devolution"),
    );
    const hasInventory = unreadNotifications.some((notification) =>
      notification.idTypeAlarm.toLowerCase().includes("inventory"),
    );
    if (hasDevolution && hasInventory) {
      return "none";
    } else if (hasDevolution) {
      return "devolution";
    } else if (hasInventory) {
      return "inventory";
    } else {
      return "none";
    }
  };

  /**
   * Función que obtiene las notificaciones y alarmas del cliente
   */
  const fetchData = async () => {
    if (userInfo) {
      const getNotifications = await getClientNotifications({
        country: userInfo.company.country,
        companyType: userInfo.company.typeCompany,
        eanCompany: userInfo.company.eanCompany,
      });

      const getAlarms = await GetClientAlarms({
        eanCompany: userInfo.company.eanCompany,
        user: userInfo.email,
      });

      const alarms =
        getAlarms.status === CODES.COD_RESPONSE_HTTP_OK &&
        (getAlarms.data.responseCode === CODES.COD_RESPONSE_SUCCESS ||
          getAlarms.data.responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST) &&
        Array.isArray(getAlarms.data.data)
          ? getAlarms.data.data
          : [];
      const combinedNotifications = [...getNotifications, ...alarms];

      // Ordena las notificaciones: primero las no leídas (read === "NO" o null), luego las leídas
      const sortedNotifications = combinedNotifications.sort((a, b) => {
        if (a.read === "NO" || a.read === null || a.alarmRead === "NO" || a.alarmRead === null)
          return -1;
        if (b.read === "NO" || b.read === null || b.alarmRead === "NO" || b.alarmRead === null)
          return 1;
        return 0;
      });

      setNotificationList(sortedNotifications);

      //Revisa las alarmas y notificaciones no leidas para saber si debe mostrar el circulo rojo
      let unreadCounter = sortedNotifications.filter(
        (notification) =>
          notification.read === "NO" ||
          notification.read === null ||
          notification.alarmRead === "NO" ||
          notification.alarmRead === null,
      ).length;
      setUnreadNotificationsCount(unreadCounter);

      const unreadAlarmsCount = alarms.filter(
        (alarm) => alarm.alarmRead === "NO" || alarm.alarmRead === null,
      ).length;

      //Condicional para abrir el modal info si el usuario acaba de logearse
      if (isInitialMount) {
        if (unreadAlarmsCount > 0) {
          setOpenModalInfo(true);
          const type = handleMessageType(
            sortedNotifications.filter(
              (notification) =>
                notification.read === "NO" ||
                notification.read === null ||
                notification.alarmRead === "NO" ||
                notification.alarmRead === null,
            ),
          );
          switch (type) {
            case "devolution":
              setTitleModalInfo(t("App.notificationsList.modalInfoTitleDevolution"));
              setContentModalInfo({
                data: { responseMessage: t("App.notificationsList.modalInfoTextDevolution") },
              });
              break;
            case "inventory":
              setTitleModalInfo(t("App.notificationsList.modalInfoTitleInventory"));
              setContentModalInfo({
                data: { responseMessage: t("App.notificationsList.modalInfoTextInventory") },
              });
              break;
            default:
              setTitleModalInfo(t("App.notificationsList.modalInfoTitle"));
              setContentModalInfo({
                data: { responseMessage: t("App.notificationsList.modalInfoText") },
              });
              break;
          }
        }
        setIsInitialMount(false);
      }
    }
  };

  /**
   * Request to get notifications
   * @returns list of companies from list of countries
   */
  const getClientNotifications = async (data) => {
    const notificationsRequest = await GetClientNotifications(data);

    if (notificationsRequest.status === CODES.COD_RESPONSE_HTTP_OK) {
      if (
        notificationsRequest.data.responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST ||
        notificationsRequest.data.responseCode === CODES.COD_RESPONSE_SUCCESS
      ) {
        const notifications = notificationsRequest.data.responseMessage;
        return notifications;
      }
    } else {
      setNotificationList([]);
    }
  };

  /**
   * Función que cierra el modal de información cuando el usuario inicia sesión y abre el panel de notificaciones
   */
  const handleCloseModalInfo = () => {
    setOpenModalInfo(false);
    setIsOpen(true);
  };

  return (
    <Box position="relative" className="step-2">
      <IconButton ref={iconRef} onClick={handleToggleNotifications}>
        <Box sx={{ position: "relative", display: "inline-block" }}>
          {isOpen ? (
            <NotificationsIcon sx={{ fontSize: 24, color: "#553ab6" }} />
          ) : (
            <NotificationsOutlinedIcon sx={{ fontSize: 24, color: "#553ab6" }} />
          )}
          {!isOpen && unreadNotificationsCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "3px", // Ajusta según sea necesario
                right: "0px", // Ajusta según sea necesario
                width: 13, // Tamaño del punto rojo
                height: 13,
                borderRadius: "50%",
                border: "2px solid #EF4444",
                bgcolor: "#EF4444",
              }}
            />
          )}
        </Box>
      </IconButton>

      {isOpen && (
        <Box
          sx={{
            position: "absolute",
            top: {
              xs: `${dropdownTop + 1}px`,
              sm: `${dropdownTop}px`,
              md: `${dropdownTop}px` + 3,
            },
            right: { xs: -100, sm: -170, md: -245 },
            width: { xs: "360px", sm: "380px", md: "400px" },
            height: `calc(100vh - ${dropdownTop}px)`,
            maxHeight: { xs: "865px", sm: "570px" },
            bgcolor: "background.paper",
            boxShadow: 3,
            borderRadius: "8px 0 0 8px",
            p: 2,
            zIndex: 1300,
            overflowY: "auto",
            textAlign: "left",
          }}
          className="scroll-table-not"
        >
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleCloseNotifications}>
              <ArrowBackIcon sx={{ color: "#553ab6", fontSize: "20px" }} />
            </IconButton>
          </Box>

          <Typography className="display-large__primary-one">
            {t("App.notificationsList.title")}
          </Typography>

          {notificationList.length > 0 ? (
            <Box mb={1} mt={1}>
              <Chip
                label={t("App.notificationsList.total", {
                  total: notificationList.length,
                })}
                color="warning"
                sx={{
                  height: "18px",
                  fontSize: "12px",
                  borderRadius: "4px",
                }}
              />
            </Box>
          ) : (
            <Grid
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              style={{ height: "100%" }}
            >
              <img
                src={noResults}
                alt="Not Match"
                className="noMatchImage_notification"
                style={{ marginBottom: 16 }}
              />
              <Divider sx={{ width: "80%", bgcolor: "#553AB6", height: "2px" }} />
              <Typography
                variant="h6"
                className="display-notification__primary-one_noNotification"
                textAlign="center"
                mt={2}
                width="80%"
              >
                {t("App.notificationsList.noNotifications")}
              </Typography>
            </Grid>
          )}

          {/* Lista de notificaciones renderizada con .map */}
          {notificationList.map((notification, index) =>
            notification.idTypeAlarm ? (
              <AlertNotificationCard key={index} alarm={notification} fetchData={fetchData} />
            ) : (
              <NotificationCard key={index} notification={notification} fetchData={fetchData} />
            ),
          )}
        </Box>
      )}
      <ModalInfo
        title={titleModalInfo}
        responseData={contentModalInfo}
        open={openModalInfo}
        onClose={handleCloseModalInfo}
        buttonText={t("App.notificationsList.buttonText")}
      />
    </Box>
  );
};

/** Componente NotificationIcon para el icono de notificaciones. */
export default NotificationIcon;
